import {
    ArrowDown,
    ArrowUp,
    ChevronsUpDown,
    EyeOff,
    Pin,
    PinOff,
    X,
} from "lucide-react"
import type {
    Column,
    Header,
    SortDirection,
    Table,
} from "@tanstack/react-table"
import * as React from "react"

import { cn } from "../../../utils"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../dropdown-menu"

interface DataGridColumnHeaderProps<TData, TValue>
    extends React.HTMLAttributes<HTMLDivElement> {
    column?: Column<TData, TValue>
    header?: Header<TData, TValue>
    table?: Table<TData>
    title?: string
}

export function DataGridColumnHeader<TData, TValue>({
    column: propColumn,
    header,
    table,
    title,
    className,
    ...props
}: DataGridColumnHeaderProps<TData, TValue>) {
    // Extract column - prefer header.column, fallback to propColumn
    const column = header?.column ?? propColumn

    // If no column or table, render simple title
    if (!column || !table) {
        return <div className={cn(className)} {...props}>{title || ''}</div>
    }

    return (
        <DataGridColumnHeaderInner
            column={column}
            header={header}
            table={table}
            title={title}
            className={className}
            {...props}
        />
    )
}

// Inner component that only renders when column and table are guaranteed to exist
function DataGridColumnHeaderInner<TData, TValue>({
    column,
    header,
    table,
    title,
    className,
    ...props
}: {
    column: Column<TData, TValue>
    header?: Header<TData, TValue>
    table: Table<TData>
    title?: string
} & React.HTMLAttributes<HTMLDivElement>) {
    // Use provided title or derive from column
    const label = title ?? (typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id)

    const isAnyColumnResizing = table.getState().columnSizingInfo.isResizingColumn

    const pinnedPosition = column.getIsPinned()
    const isPinnedLeft = pinnedPosition === "left"
    const isPinnedRight = pinnedPosition === "right"

    const onSortingChange = React.useCallback(
        (direction: SortDirection) => {
            column.toggleSorting(direction === "desc")
        },
        [column]
    )

    const onSortRemove = React.useCallback(() => {
        column.clearSorting()
    }, [column])

    const onLeftPin = React.useCallback(() => {
        column.pin("left")
    }, [column])

    const onRightPin = React.useCallback(() => {
        column.pin("right")
    }, [column])

    const onUnpin = React.useCallback(() => {
        column.pin(false)
    }, [column])

    return (
        <div className={cn("flex items-center space-x-2", className)} {...props}>
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger
                    className={cn(
                        "flex h-8 w-full items-center justify-between rounded-md py-2 pr-2 text-sm hover:bg-accent/40 data-[state=open]:bg-accent/40 focus:outline-none",
                        isAnyColumnResizing && "pointer-events-none",
                    )}
                >
                    <div className="flex flex-1 items-center gap-2">
                        <span className="truncate text-foreground">{label}</span>
                        {column.getIsSorted() === "desc" ? (
                            <ArrowDown className="h-3.5 w-3.5" />
                        ) : column.getIsSorted() === "asc" ? (
                            <ArrowUp className="h-3.5 w-3.5" />
                        ) : (
                            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />
                        )}
                    </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                    {column.getCanSort() && (
                        <>
                            <DropdownMenuItem onClick={() => onSortingChange("asc")}>
                                <ArrowUp className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                                Asc
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onSortingChange("desc")}>
                                <ArrowDown className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                                Desc
                            </DropdownMenuItem>
                            {column.getIsSorted() && (
                                <DropdownMenuItem onClick={onSortRemove}>
                                    <X className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                                    Clear Sort
                                </DropdownMenuItem>
                            )}
                        </>
                    )}

                    {column.getCanPin() && (
                        <>
                            {column.getCanSort() && <DropdownMenuSeparator />}

                            {isPinnedLeft ? (
                                <DropdownMenuItem onClick={onUnpin}>
                                    <PinOff className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                                    Unpin from left
                                </DropdownMenuItem>
                            ) : (
                                <DropdownMenuItem onClick={onLeftPin}>
                                    <Pin className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                                    Pin to left
                                </DropdownMenuItem>
                            )}

                            {isPinnedRight ? (
                                <DropdownMenuItem onClick={onUnpin}>
                                    <PinOff className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                                    Unpin from right
                                </DropdownMenuItem>
                            ) : (
                                <DropdownMenuItem onClick={onRightPin}>
                                    <Pin className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                                    Pin to right
                                </DropdownMenuItem>
                            )}
                        </>
                    )}

                    {column.getCanHide() && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
                                <EyeOff className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                                Hide
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            {header && column.getCanResize() && (
                <DataGridColumnResizer header={header} table={table} label={label} />
            )}
        </div>
    )
}

const DataGridColumnResizer = React.memo(
    DataGridColumnResizerImpl,
    (prev, next) => {
        const prevColumn = prev.header.column
        const nextColumn = next.header.column

        if (
            prevColumn.getIsResizing() !== nextColumn.getIsResizing() ||
            prevColumn.getSize() !== nextColumn.getSize()
        ) {
            return false
        }

        if (prev.label !== next.label) return false

        return true
    }
) as typeof DataGridColumnResizerImpl

interface DataGridColumnResizerProps<TData, TValue> {
    header: Header<TData, TValue>
    table: Table<TData>
    label: string
}

function DataGridColumnResizerImpl<TData, TValue>({
    header,
}: DataGridColumnResizerProps<TData, TValue>) {
    return (
        <div
            onMouseDown={header.getResizeHandler()}
            onTouchStart={header.getResizeHandler()}
            className={cn(
                "absolute right-0 top-0 h-full w-2 cursor-col-resize touch-none select-none transition-colors",
                header.column.getIsResizing()
                    ? "bg-primary"
                    : "bg-border/50 hover:bg-border opacity-0 group-hover:opacity-100"
            )}
        />
    )
}
